/**
 * Persists the GroupCart participant token across reloads inside a single
 * tab/session. Scoped to the resolved tenant slug (mirrors how the customer
 * access token is scoped) so two tabs on different tenants never collide.
 *
 * The participant token is short-lived (TTL stamped server-side) and tied to
 * a specific (tenantId, groupCartId, guestId) — losing it means the guest
 * has to rejoin via the invite code.
 */

import { scopedStorageKey } from "./storageScope";

const PARTICIPANT_KEY = "tappya_participant_token_v2";
const DEVICE_FP_KEY = "tappya_device_fp_v2";

export interface StoredParticipant {
  groupCartId: string;
  guestId: string;
  displayName: string;
  participantToken: string;
}

function isStoredParticipant(p: unknown): p is StoredParticipant {
  if (!p || typeof p !== "object") return false;
  const r = p as Record<string, unknown>;
  return (
    typeof r.groupCartId === "string" &&
    typeof r.guestId === "string" &&
    typeof r.displayName === "string" &&
    typeof r.participantToken === "string"
  );
}

export function readParticipant(): StoredParticipant | null {
  try {
    const raw = sessionStorage.getItem(scopedStorageKey(PARTICIPANT_KEY));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return isStoredParticipant(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

export function writeParticipant(p: StoredParticipant | null): void {
  try {
    const k = scopedStorageKey(PARTICIPANT_KEY);
    if (p) sessionStorage.setItem(k, JSON.stringify(p));
    else sessionStorage.removeItem(k);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Stable device fingerprint used by the backend to distinguish "the host
 * scanned again" from "a different person scanned the same QR." A random
 * UUID per browser, persisted in localStorage so it survives reloads but
 * not across browsers/devices — exactly what the dedupe heuristic needs.
 */
export function deviceFingerprint(): string {
  try {
    const k = scopedStorageKey(DEVICE_FP_KEY);
    let fp = localStorage.getItem(k);
    if (!fp) {
      fp =
        typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
          ? crypto.randomUUID()
          : `fp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(k, fp);
    }
    return fp;
  } catch {
    // Fall back to a per-call random — better than crashing the create call.
    return `fp_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }
}
